// src/domains/budget/model/program/programBudgetTables.js
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils";

/**
 * Map an internal group key from a fieldDef (e.g., "positions", "salaries")
 * to a human-readable group label used in the UI.
 *
 * @param {string | null | undefined} key
 * @returns {string}
 */
export function groupLabelFromKey(key) {
  if (!key) return "Other";
  switch (key) {
    case "positions":
      return "Positions";
    case "salaries":
      return "Salaries & Tutors";
    case "services":
      return "Services";
    case "materials":
      return "Materials & Dues";
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
}

/**
 * Build the column definitions for each ProgramBudget table.
 *
 * @typedef {Object} BuildColumnsOptions
 * @property {(row: any) => React.ReactNode} [yearRender]
 *   Optional custom renderer for the Fiscal Year cell.
 *   If omitted, defaults to BoE-style start-year formatting.
 * @property {string} [yearSortKey]
 *   Optional sort key for the year column. Defaults to "sortYear".
 *
 * @param {Array<Object>} tables
 * @param {Map<string, Object>} fieldDefMap
 * @param {BuildColumnsOptions} [options]
 * @returns {Record<string, Array<Object>>}
 */
export function buildColumnsByTableId(tables, fieldDefMap, options = {}) {
  const result = {};

  const { yearRender, yearSortKey = "sortYear" } = options || {};

  for (const table of tables) {
    const columnStyles = table.columnStyles || {};

    const cols = [
      {
        key: "year",
        label: "Fiscal Year",
        style: columnStyles.year || { width: "100px", minWidth: "100px" },
        sortKey: yearSortKey,
        editable: false,
        align: "center",
        render: yearRender
          ? (row) => yearRender(row)
          : (row) => {
              const base = formatFiscalYearFromStart(row.year);
              return row?.isProposedFallback ? `${base}` : base;
            },
      },
    ];

    for (const fieldKey of table.fields) {
      const fd = fieldDefMap.get(fieldKey);
      const isCurrency = fd?.valueType === "currency";

      cols.push({
        key: fieldKey,
        label: fd?.label || fieldKey,
        style: columnStyles[fieldKey] || { minWidth: "120px" },
        format: isCurrency ? "currency" : undefined,
        align: "right",
      });
    }

    result[table.id] = cols;
  }

  return result;
}
